import React, { Suspense } from "react";
import "./styles.css";
import "./base.css";

import Header from "./Header";
import Home from "./Home/Home";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import ABTestDebugger from "./components/ABTestDebugger";
import SeoHead from "./components/SeoHead";

import abTestingManager from "./utils/abTesting";

const Cotizador = React.lazy(() => import("./Cotizador/Cotizador"));
const Send = React.lazy(() => import("./Send"));

const App = (): JSX.Element => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isStagingSite = hostname === 'alarmitastaging.netlify.app';
    const isProductionSite = hostname === 'alarmita.com' || hostname === 'www.alarmita.com';
    const shouldUseAdminApi = isStagingSite || isProductionSite;

    const defaultDevAbConfigUrl =
        typeof window !== 'undefined'
            ? `${window.location.protocol}//${window.location.hostname}:${window.location.port === '3000' ? '3002' : '3000'}/api/ab-config`
            : 'http://localhost:3000/api/ab-config';

    const abConfigUrl =
        shouldUseAdminApi
            ? 'https://admin.alarmita.com/api/ab-config'
            : process.env.REACT_APP_AB_CONFIG_URL ||
              (process.env.NODE_ENV === 'production'
                  ? 'https://admin.alarmita.com/api/ab-config'
                  : defaultDevAbConfigUrl);

    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.toString()) {
        const params = Object.fromEntries(queryParams.entries());
        try {
            sessionStorage.setItem("queryParams", JSON.stringify(params));
        } catch { }
    }

    React.useEffect(() => {
        const url = abConfigUrl;
        if (!url) return;

        let cancelled = false;
        let idleHandle: number | undefined;
        let timeoutHandle: number | undefined;
        const idleWindow = window as Window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number;
            cancelIdleCallback?: (handle: number) => void;
        };

        const loadRemoteConfig = () => {
            fetch(url, { cache: 'default' })
                .then((response) => (response.ok ? response.json() : null))
                .then((data) => {
                    if (!cancelled && data) {
                        abTestingManager.updateRemoteConfig(data);
                    }
                })
                .catch(() => {
                    // Keep the local A/B config for the current page view.
                });
        };

        const isHomePage = typeof window !== 'undefined' && window.location.pathname === '/';

        if (isHomePage) {
            timeoutHandle = window.setTimeout(() => {
                if (idleWindow.requestIdleCallback) {
                    idleHandle = idleWindow.requestIdleCallback(loadRemoteConfig, { timeout: 2500 });
                    return;
                }

                loadRemoteConfig();
            }, 4000);
        } else {
            loadRemoteConfig();
        }

        return () => {
            cancelled = true;
            if (timeoutHandle) window.clearTimeout(timeoutHandle);
            if (idleHandle && idleWindow.cancelIdleCallback) {
                idleWindow.cancelIdleCallback(idleHandle);
            }
        };
    }, [abConfigUrl]);

    return (
        <div className="App">
            <BrowserRouter>
                <ScrollToTop />
                <SeoHead />
                <Header />
                <main id="main-content">
                    <Suspense fallback={<div className="routeFallback" aria-hidden="true" />}>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/cotizador" element={<Cotizador />} />
                            <Route path="/send" element={<Send />} />
                            <Route path="*" element={<Navigate to="/" />} />
                        </Routes>
                    </Suspense>
                </main>
                <ABTestDebugger />
            </BrowserRouter>
        </div>
    );
};

export default App;
